"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { cn } from "@newxsystems/shared";
import { useMutation } from "@tanstack/react-query";
import { Input, Label, Textarea } from "@templates/design-system";
import { type FormEvent, useId } from "react";
import { RichTextHtml } from "../../RichTextHtml";
import type { IContactFormBlockProps } from "./ContactFormBlock.props";

const formValue = (formData: FormData, key: string) => {
  const value = formData.get(key);
  return typeof value === "string" ? value.trim() : "";
};

const inputClassName =
  "min-h-12 rounded-[var(--radius-toot-panel)] border-[color-mix(in_oklab,var(--color-toot-grid-strong)_82%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-black)_74%,transparent)] px-4 text-[var(--color-toot-foreground)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] placeholder:text-[color-mix(in_oklab,var(--color-toot-muted)_74%,transparent)] focus-visible:border-[var(--color-toot-orange)] focus-visible:ring-[color-mix(in_oklab,var(--color-toot-orange)_24%,transparent)]";

const labelClassName =
  "text-[0.68rem] font-black uppercase leading-none tracking-[0.18em] text-[var(--color-toot-orange)]";

export const ContactFormBlockClient = ({
  anchorId,
  errorMessage,
  includePhone,
  messageLabel,
  phoneRequired,
  subjectLabel,
  submitLabel,
  successMessage,
}: IContactFormBlockProps) => {
  const generatedId = useId();
  const sourceId = anchorId ?? undefined;
  const fieldId = (key: string) => `${sourceId ?? generatedId}-${key}`;
  const showPhone = includePhone === true || phoneRequired === true;
  const resolvedSubjectLabel = subjectLabel ?? "Subject";
  const resolvedMessageLabel = messageLabel ?? "Message";

  const mutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const client = getBaseApiClient();

      if (!client) {
        throw new Error("Contact API client is not available.");
      }

      return client.contact.submit({
        name: formValue(formData, "name"),
        email: formValue(formData, "email"),
        phone: formValue(formData, "phone") || undefined,
        subject: formValue(formData, "subject") || undefined,
        message: formValue(formData, "message"),
        sourcePath: typeof window === "undefined" ? undefined : window.location.pathname,
        sourceBlockId: sourceId,
      });
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutation.mutate(new FormData(event.currentTarget));
  };

  if (mutation.isSuccess) {
    return successMessage ? (
      <div
        className="toot-heat-panel relative isolate overflow-hidden rounded-[var(--radius-toot-frame)] border-[color-mix(in_oklab,var(--color-toot-orange)_54%,var(--color-toot-grid-strong))] bg-[radial-gradient(circle_at_100%_0%,color-mix(in_oklab,var(--color-toot-red)_18%,transparent),transparent_16rem),linear-gradient(180deg,var(--color-toot-surface-raised),var(--color-toot-black))] p-5 shadow-2xl shadow-black/45 md:p-7"
        role="status"
      >
        <div
          aria-hidden="true"
          className="mb-5 h-2 w-2 bg-[var(--color-toot-red)] shadow-[0_0_1.1rem_rgba(255,77,46,0.82)]"
        />
        <RichTextHtml
          className="prose max-w-none text-base font-semibold leading-7 text-[color-mix(in_oklab,var(--color-toot-foreground)_92%,transparent)]"
          html={successMessage}
        />
      </div>
    ) : null;
  }

  return (
    <div className="toot-heat-panel relative isolate overflow-hidden rounded-[var(--radius-toot-frame)] p-4 shadow-2xl shadow-black/45 sm:p-5 lg:p-6">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,var(--color-toot-orange),transparent)] opacity-70"
      />
      <form className="relative z-10 grid gap-5" onSubmit={handleSubmit}>
        <div className="grid gap-2">
          <Label className={labelClassName} htmlFor={fieldId("name")}>
            Name
          </Label>
          <Input
            autoComplete="name"
            className={inputClassName}
            id={fieldId("name")}
            maxLength={160}
            name="name"
            required
            type="text"
          />
        </div>

        <div className="grid gap-2">
          <Label className={labelClassName} htmlFor={fieldId("email")}>
            Email
          </Label>
          <Input
            autoComplete="email"
            className={inputClassName}
            id={fieldId("email")}
            maxLength={254}
            name="email"
            required
            type="email"
          />
        </div>

        {showPhone ? (
          <div className="grid gap-2">
            <Label className={labelClassName} htmlFor={fieldId("phone")}>
              Phone{phoneRequired === true ? null : " (optional)"}
            </Label>
            <Input
              autoComplete="tel"
              className={inputClassName}
              id={fieldId("phone")}
              maxLength={80}
              name="phone"
              required={phoneRequired === true}
              type="tel"
            />
          </div>
        ) : null}

        <div className="grid gap-2">
          <Label className={labelClassName} htmlFor={fieldId("subject")}>
            {resolvedSubjectLabel}
          </Label>
          <Input
            className={inputClassName}
            id={fieldId("subject")}
            maxLength={180}
            name="subject"
            type="text"
          />
        </div>

        <div className="grid gap-2">
          <Label className={labelClassName} htmlFor={fieldId("message")}>
            {resolvedMessageLabel}
          </Label>
          <Textarea
            className={cn(inputClassName, "min-h-44 resize-y py-3 leading-6")}
            id={fieldId("message")}
            maxLength={5000}
            minLength={1}
            name="message"
            required
            rows={7}
          />
        </div>

        {mutation.isError && errorMessage ? (
          <p
            className="rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-red)_58%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-red)_14%,var(--color-toot-black))] px-4 py-3 text-sm font-bold leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_92%,var(--color-toot-orange))]"
            role="alert"
          >
            {errorMessage}
          </p>
        ) : null}

        <button
          className={cn(
            "toot-button toot-button-primary w-full cursor-pointer disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55",
            mutation.isPending && "cursor-wait",
          )}
          disabled={mutation.isPending}
          type="submit"
        >
          <span>{submitLabel}</span>
          <span aria-hidden="true">→</span>
        </button>
      </form>
    </div>
  );
};

ContactFormBlockClient.displayName = "Block::ContactFormBlockClient";
